/**
 * What the dashboard's failure reporting ($exception from api.ts and
 * api_request_failed from request-failures.ts) leaves out, decided once so the
 * two cannot disagree.
 *
 * 1. A request its caller marked as having an expected failure: a probe whose
 *    failure IS the answer (does this customer's link open?). See apiFetch's
 *    `reportFailure: false`.
 *
 * 2. A network failure (no response at all) that is the person's own
 *    connection, not the server: the browser says it is offline, the tab is
 *    hidden (a sleeping laptop resumes with every poll failing at once), or the
 *    page is being unloaded. And past those, a burst: when the connection
 *    drops, every route fails in the same second, so after one network failure
 *    is reported the rest are held for NETWORK_BURST_MS. The first still goes
 *    out, so a real outage stays visible.
 *
 * Failures with a status are never gated here.
 */

export const NETWORK_BURST_MS = 60_000;

export type NetworkReportChannel = "exception" | "request";

const unreported = new WeakSet<object>();
const lastReportedAt = new Map<NetworkReportChannel, number>();
let unloading = false;

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("pagehide", () => {
    unloading = true;
  });
  // A page restored from the back/forward cache is live again.
  window.addEventListener("pageshow", () => {
    unloading = false;
  });
}

/** Returns a copy of `init` whose failure is never reported. The copy keeps a shared init (`del`) unmarked. */
export function unreportedInit(init: RequestInit | undefined): RequestInit {
  const copy: RequestInit = { ...init };
  unreported.add(copy);
  return copy;
}

export function isUnreported(init: RequestInit | undefined): boolean {
  return !!init && unreported.has(init);
}

function connectionLikelyGone(): boolean {
  if (unloading) return true;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/**
 * Whether this network failure should be reported on `channel`. Each channel
 * keeps its own burst window: a GET's first attempt reaches api_request_failed
 * seconds before its retries run out and reach $exception, and the one must not
 * swallow the other.
 */
export function shouldReportNetworkFailure(channel: NetworkReportChannel, now: number = Date.now()): boolean {
  if (connectionLikelyGone()) return false;
  const last = lastReportedAt.get(channel);
  if (last !== undefined && now - last < NETWORK_BURST_MS) return false;
  lastReportedAt.set(channel, now);
  return true;
}

/** Test seam. */
export function __resetReportGateForTests(): void {
  lastReportedAt.clear();
  unloading = false;
}
